import { useEffect, useRef } from 'react';
import { backendURL, backendWS } from '../IPaddress';

const getWebSocketBaseUrl = () => {
  if (backendWS) {
    return backendWS;
  }

  if (backendURL) {
    return backendURL.replace(/^http/, 'ws');
  }

  return null;
};

const buildRoomUrl = (roomName, isPublic) => {
  const baseUrl = getWebSocketBaseUrl();
  if (!baseUrl || !roomName) {
    return null;
  }

  const encodedRoom = encodeURIComponent(roomName);
  return `${baseUrl}${isPublic ? '/ws/public/' : '/ws/'}${encodedRoom}`;
};

const useWebSocketRoom = ({
  roomName,
  enabled = true,
  isPublic = false,
  reconnectDelayMs = 2000,
  onMessage,
  onOpen,
  onClose,
  onError,
}) => {
  const messageHandlerRef = useRef(onMessage);
  const openHandlerRef = useRef(onOpen);
  const closeHandlerRef = useRef(onClose);
  const errorHandlerRef = useRef(onError);

  useEffect(() => {
    messageHandlerRef.current = onMessage;
    openHandlerRef.current = onOpen;
    closeHandlerRef.current = onClose;
    errorHandlerRef.current = onError;
  }, [onMessage, onOpen, onClose, onError]);

  useEffect(() => {
    if (!enabled || !roomName) {
      return undefined;
    }

    const socketUrl = buildRoomUrl(roomName, isPublic);
    if (!socketUrl) {
      return undefined;
    }

    let socket = null;
    let reconnectTimeout = null;
    let shouldReconnect = true;

    const connect = () => {
      socket = new WebSocket(socketUrl);

      socket.onopen = () => {
        if (openHandlerRef.current) {
          openHandlerRef.current();
        }
      };

      socket.onmessage = (event) => {
        if (!messageHandlerRef.current) {
          return;
        }

        try {
          const parsedMessage = JSON.parse(event.data);
          messageHandlerRef.current(parsedMessage);
        } catch (error) {
          console.error('WebSocket parse error:', error);
        }
      };

      socket.onerror = (error) => {
        if (errorHandlerRef.current) {
          errorHandlerRef.current(error);
        }
      };

      socket.onclose = (event) => {
        if (closeHandlerRef.current) {
          closeHandlerRef.current(event);
        }

        if (shouldReconnect) {
          reconnectTimeout = window.setTimeout(connect, reconnectDelayMs);
        }
      };
    };

    connect();

    return () => {
      shouldReconnect = false;
      if (reconnectTimeout) {
        window.clearTimeout(reconnectTimeout);
      }
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, [enabled, isPublic, reconnectDelayMs, roomName]);
};

export default useWebSocketRoom;
